import { t } from '@/i18n'
import type { BillWithPayments } from '@/types/bill'

import {
  AdditionalInfo,
  Barcode,
  Box,
  Company,
  DottedSeparator,
  DoubleInfoItem,
  DoubleLabel,
  If,
  InfoItem,
  Label,
  Logo,
  PaymentsBox,
  Product,
  Section,
  Separator,
  TaxesBreakdown,
  Title,
  Total
} from './components'
import taxation from './taxation'

function pickupTypeLabel(type?: 'delivery' | 'ownDelivery' | 'takeAway' | null): string | null {
  if (!type) return null
  switch (type) {
    case 'delivery':
      return t('bill.external-delivery')
    case 'ownDelivery':
      return t('bill.own-delivery')
    case 'takeAway':
      return t('bill.take-away')
    default:
      return 'restaurant'
  }
}

function paymentLabel(payment: { type: string }): string {
  switch (payment.type) {
    case 'cash':
      return t('bill.cash')
    case 'card':
      return t('bill.card')
    case 'dataphone':
      return t('bill.dataphone')
    default:
      return payment.type
  }
}

function header(bill: BillWithPayments, options: { logoImageId?: string }) {
  return [
    new Logo(options.logoImageId!),
    new Company(bill.company),
    new AdditionalInfo(bill),
    new If(
      bill.activationTime,
      new Label(bill.activationTime, { align: 'center', valueType: 'date', style: '16px' })
    ),
    new DoubleLabel(
      bill.number ? t('bill.simplified-invoice') : t('bill.proforma-invoice'),
      bill.number ?? ''
    ),
    new If(
      bill.source !== 'Restaurant' || !bill.tableName || bill.pickupType,
      new Title(bill.code!)
    ),
    new If(
      bill.source !== 'Restaurant' && bill.tableName,
      new Label(`${t('bill.code')} ${bill.source}: ${bill.tableName}`, {
        lineHeight: 28
      })
    ),
    new If(
      bill.source !== 'Restaurant',
      new DoubleInfoItem(
        t('bill.method'),
        pickupTypeLabel(bill.pickupType)!,
        t('bill.diners'),
        bill.diners
      )
    ),
    new If(
      bill.source === 'Restaurant',
      new DoubleInfoItem(
        bill.pickupType ? t('bill.name') : t('bill.table'),
        bill.tableName,
        t('bill.diners'),
        bill.diners
      )
    ),
    new If(
      bill.schedulingTime || bill.pickupType,
      new InfoItem(t('bill.scheduling-time'), bill.schedulingTime || t('bill.asap'), {
        keepTogether: true,
        valueType: bill.schedulingTime ? 'date' : 'text'
      })
    )
  ]
}

function customerDetails(bill: BillWithPayments) {
  const shouldShowCustomerAddress = bill.pickupType === 'ownDelivery'
  return [
    new InfoItem(t('tabs.customer-name'), bill.customerName, {
      important: true,
      keepTogether: true
    }),
    new If(
      shouldShowCustomerAddress,
      new InfoItem(t('tabs.address'), bill.customerAddress, {
        keepTogether: true
      })
    ),
    new If(
      shouldShowCustomerAddress,
      new InfoItem(t('tabs.address-details'), bill.customerAddressDetails, {
        keepTogether: true
      })
    ),
    new If(
      shouldShowCustomerAddress,
      new InfoItem(t('tabs.postal-code'), bill.customerPostalCode, {
        keepTogether: true
      })
    ),
    new InfoItem(t('tabs.phone-number'), bill.customerPhoneNumber, {
      keepTogether: true
    }),
    new InfoItem(t('tabs.phone-number-code'), bill.customerPhoneNumberCode, {
      keepTogether: true
    })
  ]
}

function invoiceDetails(bill: {
  customerCompany?: { name?: string; taxId?: string; address?: string }
}) {
  if (bill.customerCompany) {
    return [
      new Section(t('bill.invoice-details')),
      new InfoItem(t('bill.company'), bill.customerCompany.name),
      new InfoItem(t('bill.tax-id'), bill.customerCompany.taxId),
      new InfoItem(t('bill.fiscal-address'), bill.customerCompany.address)
    ]
  }
  return []
}

function products(bill: BillWithPayments) {
  return [
    new Section(t('bill.products')),
    ...bill.products.map(product => new Product(product, bill.pickupType)),
    new InfoItem(t('bill.allergies'), bill.allergyInfo),
    new If(bill.needCutlery, new Box(t('bill.need-cutlery')))
  ]
}

function breakdown(bill: BillWithPayments, shouldShowPendingPayment: boolean) {
  const taxRates =
    bill.taxRates.length > 0
      ? bill.taxRates
      : [
          {
            taxPercentage: bill.taxPercentage,
            taxableBase: bill.taxableBase,
            tax: bill.tax,
            total: bill.total
          }
        ]
  return [
    new TaxesBreakdown(taxRates, bill.taxLabel!),
    new If(
      !shouldShowPendingPayment,
      new Total(bill.total, bill.diners!, bill.type),
      new InfoItem(t('bill.total'), bill.total, {
        valueType: 'currency'
      })
    )
  ]
}

function feesAndDiscounts(bill: BillWithPayments) {
  let discount = null
  if (bill.discount && bill.discountTotal! > 0) {
    const discountName = bill.discount.concept ? bill.discount.concept : ''
    if (bill.discount.type === 'percentage') {
      discount = new InfoItem(
        `${t('bill.discount')} ${discountName} (${bill.discount.amount}%)`,
        -bill.discountTotal!,
        { valueType: 'currency', light: true }
      )
    } else if (bill.discount.type === 'currency') {
      discount = new InfoItem(t('bill.discount') + ' ' + discountName, -bill.discountTotal!, {
        valueType: 'currency',
        light: true
      })
    }
  }

  let deliveryFee = null
  if (bill.deliveryFee) {
    deliveryFee = new InfoItem(t('bill.delivery-fee'), bill.deliveryFee, {
      valueType: 'currency',
      light: true
    })
  } else if (bill.discount?.freeDelivery) {
    deliveryFee = new InfoItem(t('bill.delivery-fee'), t('bill.free-delivery').toUpperCase(), {
      light: true
    })
  }

  return [
    new If(
      bill.terraceSurcharge,
      new InfoItem(
        `${t('bill.terrace-surcharge')} (${bill.terraceSurchargePercentage}%)`,
        bill.terraceSurcharge,
        {
          valueType: 'currency',
          light: true
        }
      )
    ),
    new If(deliveryFee, deliveryFee!),
    new If(
      bill.minimumBasketSurcharge,
      new InfoItem(t('bill.minimum-basket-surcharge'), bill.minimumBasketSurcharge, {
        valueType: 'currency',
        light: true
      })
    ),
    new If(discount, discount!),
    new If(
      bill.terraceSurcharge || deliveryFee || bill.minimumBasketSurcharge || discount,
      new DottedSeparator()
    )
  ]
}

function payments(bill: BillWithPayments, shouldShowPendingPayment: boolean) {
  const negativePayments = bill.payments.filter(payment => payment.amount < 0)
  const allPayments = bill.payments.filter(payment => payment.amount > 0)
  const paidAmount = bill.payments.reduce(
    (sum, payment) => sum + payment.amount - (payment.tip || 0),
    0
  )
  const tipAmount =
    bill.payments.reduce((sum, payment) => sum + (payment.tip || 0), 0) +
    Math.max(0, paidAmount - bill.total)

  const sections = []
  if (negativePayments.length > 0) {
    const mappedPayments = negativePayments.map(payment => {
      return {
        label: paymentLabel(payment),
        amount: -payment.amount
      }
    })
    sections.push(
      new PaymentsBox(t('bill.extra-charges'), mappedPayments, {
        padding: 0,
        border: false
      })
    )
  }
  if (allPayments.length > 0) {
    const mappedPayments = allPayments.map(payment => {
      return {
        label: paymentLabel(payment),
        amount: payment.amount,
        change: payment.change,
        tip: payment.tip
      }
    })
    sections.push(
      new PaymentsBox(t('bill.payments'), mappedPayments, {
        padding: 0,
        border: false
      })
    )
  }
  if (tipAmount > 0) {
    sections.push(
      new InfoItem(t('bill.tip'), tipAmount, {
        valueType: 'currency',
        light: true
      })
    )
  }
  if (shouldShowPendingPayment) {
    sections.push(
      new PaymentsBox(
        t('bill.payment-pending'),
        [
          {
            label: paymentLabel({
              type: bill.preferredPaymentMethod || t('bill.total')
            }),
            amount: bill.total - paidAmount
          }
        ],
        { important: true }
      )
    )
  }
  return sections
}

function taxExemption(isTaxExempt?: boolean, taxExemptionReason?: string) {
  if (!isTaxExempt || !taxExemptionReason) return []
  return [new Label(taxExemptionReason, { style: 'italic 12px', lineHeight: 14 })]
}

async function generateBill(
  bill: BillWithPayments,
  options: {
    barcode?: boolean
    isTaxExempt?: boolean
    taxExemptionReason?: string
    hospitality: boolean
    logoImageId?: string
  }
) {
  const paidAmount = bill.payments.reduce((sum, payment) => sum + payment.amount, 0)
  const shouldShowPendingPayment =
    bill.total > 0 && bill.total > paidAmount && (!!bill.number || !!bill.preferredPaymentMethod)
  const taxComponents = taxation(bill, options)

  return [
    ...taxComponents.header,
    ...header(bill, options),
    ...customerDetails(bill),
    ...invoiceDetails(bill),
    new If(bill.note, new Box(t('bill.note'), bill.note!)),
    ...products(bill),
    new Separator(),
    ...feesAndDiscounts(bill),
    ...breakdown(bill, shouldShowPendingPayment),
    ...payments(bill, shouldShowPendingPayment),
    new If(
      bill.code && options.barcode && bill.pickupType,
      new Barcode(bill.tabId ? bill.tabId.slice(-10) : bill.code)
    ),
    ...taxExemption(options.isTaxExempt, options.taxExemptionReason),
    ...taxComponents.footer
  ]
}

export default generateBill
